import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import CandidateCard from './components/CandidateCard';
import CandidateTable from './components/CandidateTable';
import FilterPanel from './components/FilterPanel';
import BulkActionsBar from './components/BulkActionsBar';
import PipelineView from './components/PipelineView';
import CandidateProfileModal from './components/CandidateProfileModal';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';

const CandidateManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState('table');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    experience: [],
    status: [],
    location: [],
    skillMatch: '',
    minSalary: '',
    maxSalary: ''
  });

  const [candidates, setCandidates] = useState([]);

  const viewModeOptions = [
    { value: 'table', label: 'Table View' },
    { value: 'cards', label: 'Card View' },
    { value: 'pipeline', label: 'Pipeline View' }
  ];

  useEffect(() => {
    if (user?.id) {
      fetchCandidates();
    }
  }, [user]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      
      // Fetch job applications with applicant profile data
      const { data: applications, error } = await supabase?.from('job_applications')?.select(`
          id,
          applicant_id,
          job_id,
          full_name,
          email,
          phone,
          location,
          experience,
          expected_ctc,
          current_ctc,
          skills_assessment,
          relevant_skills,
          linkedin,
          portfolio,
          status,
          applied_at,
          notes,
          why_interested,
          notice_period,
          open_to_remote,
          willing_to_relocate,
          applicant:user_profiles!job_applications_applicant_id_fkey (
            profile_photo,
            bio,
            skills,
            preferred_locations,
            remote_preference
          ),
          job:jobs!job_applications_job_id_fkey (
            job_title,
            employer:user_profiles!jobs_employer_id_fkey (
              company_name
            )
          )
        `)?.order('applied_at', { ascending: false });

      if (error) throw error;

      // Transform data to match component structure
      const transformedCandidates = applications?.map((app) => ({
        id: app?.id,
        applicantId: app?.applicant_id,
        jobId: app?.job_id,
        name: app?.full_name,
        avatar: app?.applicant?.profile_photo || '',
        avatarAlt: `Professional headshot of ${app?.full_name}`,
        position: app?.job?.job_title || 'Position not specified',
        companyName: app?.job?.employer?.company_name || 'Company not specified',
        location: app?.location,
        experience: app?.experience,
        expectedSalary: app?.expected_ctc,
        currentSalary: app?.current_ctc,
        skillMatch: calculateSkillMatch(app?.relevant_skills, app?.skills_assessment),
        rating: calculateRating(app?.status, app?.skills_assessment),
        status: app?.status,
        appliedDate: formatDate(app?.applied_at),
        email: app?.email,
        phone: app?.phone,
        skills: parseSkills(app?.relevant_skills, app?.applicant?.skills),
        summary: app?.why_interested || app?.applicant?.bio || '',
        notes: app?.notes || [],
        linkedin: app?.linkedin || '',
        portfolio: app?.portfolio || '',
        noticePeriod: app?.notice_period || '',
        openToRemote: app?.open_to_remote || false,
        willingToRelocate: app?.willing_to_relocate || false,
        remotePreference: app?.applicant?.remote_preference || '',
        preferredLocations: app?.applicant?.preferred_locations || [],
        activity: generateActivity(app)
      })) || [];

      setCandidates(transformedCandidates);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSkillMatch = (relevantSkills, skillsAssessment) => {
    if (!skillsAssessment || !Array.isArray(skillsAssessment)) return 0;
    
    const totalRating = skillsAssessment?.reduce((sum, skill) => {
      return sum + (parseInt(skill?.rating) || 0);
    }, 0);
    
    const averageRating = skillsAssessment?.length > 0 
      ? totalRating / skillsAssessment?.length 
      : 0;
    
    return Math.round((averageRating / 5) * 100);
  };

  const calculateRating = (status, skillsAssessment) => {
    let baseRating = 3;
    
    if (status === 'shortlisted' || status === 'interview') baseRating = 4;
    if (status === 'accepted' || status === 'hired') baseRating = 5;
    
    const skillMatch = calculateSkillMatch('', skillsAssessment);
    if (skillMatch >= 90) baseRating = Math.min(5, baseRating + 1);
    
    return baseRating;
  };

  const parseSkills = (relevantSkills, profileSkills) => {
    const skills = [];
    
    if (relevantSkills) {
      const skillsArray = relevantSkills?.split(',')?.map(s => s?.trim());
      skills?.push(...skillsArray);
    }
    
    if (profileSkills && Array.isArray(profileSkills)) {
      skills?.push(...profileSkills);
    }
    
    return [...new Set(skills)];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })?.replace(/\//g, '/');
  };

  const generateActivity = (application) => {
    const activity = [
      {
        icon: 'FileText',
        description: 'Application submitted',
        timestamp: formatDate(application?.applied_at)
      }
    ];

    if (application?.status !== 'pending') {
      activity?.push({
        icon: 'CheckCircle2',
        description: 'Application reviewed',
        timestamp: formatDate(application?.updated_at || application?.applied_at)
      });
    }

    if (application?.status === 'shortlisted') {
      activity?.push({
        icon: 'Star',
        description: 'Shortlisted for interview',
        timestamp: formatDate(application?.updated_at || application?.applied_at)
      });
    }

    if (application?.status === 'interview') {
      activity?.push({
        icon: 'Calendar',
        description: 'Interview scheduled',
        timestamp: formatDate(application?.updated_at || application?.applied_at)
      });
    }

    return activity;
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      experience: [],
      status: [],
      location: [],
      skillMatch: '',
      minSalary: '',
      maxSalary: ''
    });
  };

  const handleSelectCandidate = (candidateId, checked) => {
    if (checked) {
      setSelectedCandidates((prev) => [...prev, candidateId]);
    } else {
      setSelectedCandidates((prev) => prev?.filter((id) => id !== candidateId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCandidates(candidates?.map((c) => c?.id));
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleStatusChange = async (candidateId, newStatus) => {
    try {
      // Update in Supabase
      const { error } = await supabase?.from('job_applications')?.update({ status: newStatus })?.eq('id', candidateId);

      if (error) throw error;

      // Update local state
      setCandidates((prev) =>
        prev?.map((candidate) =>
          candidate?.id === candidateId
            ? { ...candidate, status: newStatus }
            : candidate
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkStatusChange = (newStatus) => {
    setCandidates((prev) =>
    prev?.map((candidate) =>
    selectedCandidates?.includes(candidate?.id) ?
    { ...candidate, status: newStatus } :
    candidate
    )
    );
    setSelectedCandidates([]);
  };

  const handleViewProfile = (candidateId) => {
    const candidate = candidates?.find((c) => c?.id === candidateId);
    setSelectedCandidate(candidate);
    setShowProfileModal(true);
  };

  const handleScheduleInterview = (candidateId) => {
    const candidate = candidates?.find((c) => c?.id === candidateId);
    if (candidate) {
      setCandidateToSchedule(candidate);
      setShowScheduleModal(true);
      // Close profile modal when opening schedule modal
      setShowProfileModal(false);
    }
  };

  const handleInterviewScheduled = () => {
    fetchCandidates();
  };

  const handleSendMessage = (candidateId) => {
    alert(`Send message to candidate ID: ${candidateId}`);
  };

  const handleBulkEmail = () => {
    alert(`Send email to ${selectedCandidates?.length} selected candidates`);
  };

  const handleBulkExport = () => {
    alert(`Export ${selectedCandidates?.length} selected candidates`);
  };

  const handleAddNote = (candidateId, noteContent) => {
    setCandidates((prev) =>
    prev?.map((candidate) =>
    candidate?.id === candidateId ?
    {
      ...candidate,
      notes: [
      ...(candidate?.notes || []),
      {
        date: new Date()?.toLocaleDateString('en-GB'),
        author: 'Current User',
        content: noteContent
      }]

    } :
    candidate
    )
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading candidates...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Candidate Management
              </h1>
              <p className="text-sm text-muted-foreground">
                Review and manage job applications across all positions
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Select
                options={viewModeOptions}
                value={viewMode}
                onChange={setViewMode}
                className="w-40" />


              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left">

                Export
              </Button>

              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/post-new-job')}>

                Post New Job
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Applications</span>
                <Icon name="Users" size={20} className="text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">{candidates?.length}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Shortlisted</span>
                <Icon name="Star" size={20} className="text-purple-500" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {candidates?.filter((c) => c?.status === 'shortlisted')?.length}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Interviews</span>
                <Icon name="Calendar" size={20} className="text-indigo-500" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {candidates?.filter((c) => c?.status === 'interview_scheduled' || c?.status === 'interviewed')?.length}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Offers</span>
                <Icon name="Gift" size={20} className="text-green-500" />
              </div>
              <p className="text-2xl font-bold text-foreground">
                {candidates?.filter((c) => c?.status === 'offered' || c?.status === 'hired')?.length}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters} />

            </div>

            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">
                      Candidates ({candidates?.length})
                    </h2>
                    {selectedCandidates?.length > 0 &&
                    <span className="text-sm text-muted-foreground">
                        {selectedCandidates?.length} selected
                      </span>
                    }
                  </div>
                </div>

                <div className="p-4">
                  {viewMode === 'table' &&
                  <CandidateTable
                    candidates={candidates}
                    selectedCandidates={selectedCandidates}
                    onSelectCandidate={handleSelectCandidate}
                    onSelectAll={handleSelectAll}
                    onStatusChange={handleStatusChange}
                    onViewProfile={handleViewProfile}
                    onScheduleInterview={handleScheduleInterview}
                    onSendMessage={handleSendMessage} />

                  }

                  {viewMode === 'cards' &&
                  <div className="space-y-4">
                      {candidates?.map((candidate) =>
                    <CandidateCard
                      key={candidate?.id}
                      candidate={candidate}
                      onStatusChange={handleStatusChange}
                      onViewProfile={handleViewProfile}
                      onScheduleInterview={handleScheduleInterview}
                      onSendMessage={handleSendMessage} />

                    )}
                    </div>
                  }

                  {viewMode === 'pipeline' &&
                  <PipelineView
                    candidates={candidates}
                    onStatusChange={handleStatusChange}
                    onViewProfile={handleViewProfile} />

                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <BulkActionsBar
        selectedCount={selectedCandidates?.length}
        onBulkStatusChange={handleBulkStatusChange}
        onBulkEmail={handleBulkEmail}
        onBulkExport={handleBulkExport}
        onClearSelection={() => setSelectedCandidates([])} />

      {showProfileModal &&
      <CandidateProfileModal
        candidate={selectedCandidate}
        onClose={() => setShowProfileModal(false)}
        onStatusChange={handleStatusChange}
        onScheduleInterview={handleScheduleInterview}
        onAddNote={handleAddNote} />
      }

      {showScheduleModal && candidateToSchedule &&
      <ScheduleInterviewModal
        candidate={candidateToSchedule}
        onClose={() => {
          setShowScheduleModal(false);
          setCandidateToSchedule(null);
        }}
        onInterviewScheduled={handleInterviewScheduled} />
      }
    </div>
  );
};

export default CandidateManagement;